import { Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Subject } from 'rxjs';
import { isReceiveMessageTypeValid, TChatbotReceiveMessage, TChatbotSendMessage } from '../types/chatbot-api.types';
import {
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_SILENCE_TIMEOUT_MS,
  RECONNECT_BASE_DELAY_MS,
  RECONNECT_MAX_DELAY_MS,
} from '../types/chatbot.constants';
import { TChatbotConnectionStatus } from '../types/chatbot.types';
import { ChatbotEnvironmentService } from './chatbot-environment.service';

/**
 * The socket is opened on app load rather than on first use, because it will
 * also carry notifications.
 * This is not to be used directly, public api for components
 * is provided via `ChatbotSessionService`.
 * */
@Injectable({ providedIn: 'root' })
export class ChatbotWebSocketService {
  readonly #status = new BehaviorSubject<TChatbotConnectionStatus>('off');
  readonly #messages = new Subject<TChatbotReceiveMessage>();

  readonly status$ = this.#status.asObservable();
  readonly messages$ = this.#messages.asObservable();
  readonly status = toSignal(this.status$, { initialValue: 'off' as TChatbotConnectionStatus });

  #socket: WebSocket | null = null;
  #reconnectAttempt = 0;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  #silenceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private chatbotEnvironment: ChatbotEnvironmentService) {}

  connect(): void {
    if (this.#isSocketLive()) return;

    this.#clearReconnectTimer();
    this.#openSocket();
  }

  send(message: TChatbotSendMessage): boolean {
    if (this.#socket?.readyState === WebSocket.OPEN) {
      this.#socket.send(JSON.stringify(message));
      return true;
    }

    this.#reconnectAttempt = 0;
    this.connect();
    return false;
  }

  disconnect(): void {
    this.#reconnectAttempt = 0;

    this.#clearReconnectTimer();
    this.#stopHeartbeat();
    this.#socket?.close();
    this.#socket = null;
    this.#status.next('off');
  }

  #isSocketLive(): boolean {
    return this.#socket?.readyState === WebSocket.CONNECTING || this.#socket?.readyState === WebSocket.OPEN;
  }

  #openSocket(): void {
    this.#status.next('connecting');

    const socket = new WebSocket(this.chatbotEnvironment.getWebSocketUrl());
    this.#socket = socket;

    socket.onopen = () => {
      this.#reconnectAttempt = 0;
      this.#status.next('open');
      this.#startHeartbeat();
    };

    socket.onmessage = (event) => {
      if (this.#socket !== socket) return;

      this.#clearSilenceTimer();

      try {
        const frame = JSON.parse(event.data as string);
        if (isReceiveMessageTypeValid(frame?.type)) this.#messages.next(frame);
      } catch {
        console.error('[ChatbotWebSocketService] Could not parse frame', event.data);
      }
    };

    socket.onclose = () => {
      if (this.#socket !== socket) return;

      this.#stopHeartbeat();
      this.#socket = null;
      this.#scheduleReconnect();
    };
  }

  #scheduleReconnect(): void {
    if (this.#reconnectTimer) return;

    const backoff = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** this.#reconnectAttempt, RECONNECT_MAX_DELAY_MS);
    // jitter so a server blip does not bring every client back on the exact same time
    const delay = backoff / 2 + Math.random() * (backoff / 2);

    if (backoff < RECONNECT_MAX_DELAY_MS) this.#reconnectAttempt += 1;
    this.#status.next('connecting');
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null;
      this.#openSocket();
    }, delay);
  }

  #startHeartbeat(): void {
    this.#stopHeartbeat();

    this.#heartbeatTimer = setInterval(() => {
      if (this.#socket?.readyState !== WebSocket.OPEN) return;

      this.send({ type: 'ping' });

      // `#silenceTimer` is cleared on every message received
      this.#silenceTimer ??= setTimeout(() => {
        this.#silenceTimer = null;
        this.#socket?.close();
      }, HEARTBEAT_SILENCE_TIMEOUT_MS);
    }, HEARTBEAT_INTERVAL_MS);
  }

  #stopHeartbeat(): void {
    if (this.#heartbeatTimer) clearInterval(this.#heartbeatTimer);
    this.#heartbeatTimer = null;
    this.#clearSilenceTimer();
  }

  #clearSilenceTimer(): void {
    if (this.#silenceTimer) clearTimeout(this.#silenceTimer);
    this.#silenceTimer = null;
  }

  #clearReconnectTimer(): void {
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = null;
  }
}
